// The Upcoming view of the Deliverables tab: the dates owed in the next 7 / 14 /
// 30 days (or a custom span), late ones pinned first whatever the range, each
// with its inline verb (Submit, or Mark held for a milestone).
//
// Two ListOccurrences feed it: the range, and every late date (a lookahead that
// hid them would read as "all clear"). Team, range and search live in the URL.
//
// Event-driven (?events=1) swaps the dated table for the deliverables with no
// firm date until something happens: from pangaea's own undated reasons
// (ListDuties), the ones waiting on an event nobody logged and the on_request
// ones, each with the verb that logs it (the DutyPage's, same labels).
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraDateField, SpecraEmptyState, SpecraInput, SpecraListPage, SpecraSegmentedControl, SpecraSelect, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useGlossaryCasing } from '../../Glossary/GlossaryText';
import { useContractCLINs } from '../../Programs/contractHooks';
import { useCmAccess } from '../access';
import { useCmDrawerParams } from '../drawerParams';
import { OccurrenceWindow, useOccurrences, useWorkstreamDetails } from '../hooks';
import { dutyClinDisplay } from '../lib/dutyClin';
import { addDays, cmDate, todayISO } from '../lib/format';
import { describeRule, eventLabelIn } from '../lib/rule';
import { isOccurrenceState, isOpenState, STATE_SHORT_LABEL } from '../lib/status';
import { filterDateRows, normalizeTeamId, resolveOccurrencePeriodId } from '../lib/tableControls';
import { useCmLookups } from '../lookups';
import { clearAllAction, useColumnView } from './ColumnMenu';
import { CmLatestSubmission, CmOccurrenceStatus, CmTable, occurrenceClinDisplay } from './common';
import { LogEventDrawer } from './LogEventDrawer';
const DEFAULT_RANGE = '14';
const CONTRACT_WIDE = 'contract-wide';
/** Kinds no one logs here: the reviewer dates receipt of order; triggers and "alongside" follow another deliverable; the contract's own rows date the rest. */ const NOT_A_LOG_EVENT = /^(receipt_of_order|deliverable_(submitted|accepted|due)_|(award|option_period_start|exercise_start|modification_effective|exercise|optional_task_exercise|pop_start|pop_end|period_start|period_end)$)/;
// Late first, then soonest due; ties by id so a refetch never reshuffles rows.
function byUrgency(a, b) {
    const al = a.status?.state === 'late' ? 0 : 1;
    const bl = b.status?.state === 'late' ? 0 : 1;
    if (al !== bl) return al - bl;
    return (a.dueOn || '9999').localeCompare(b.dueOn || '9999') || a.id.localeCompare(b.id);
}
export function UpcomingDeliverables({ programId, header }) {
    const access = useCmAccess(programId);
    const params = useCmDrawerParams();
    const lk = useCmLookups(programId);
    const clins = useContractCLINs(programId).data?.clins ?? [];
    const [sp, setSp] = useSearchParams();
    const team = sp.get('team') ?? '';
    const range = [
        '7',
        '14',
        '30',
        'custom'
    ].includes(sp.get('range') ?? '') ? sp.get('range') : DEFAULT_RANGE;
    const from = sp.get('from') ?? '';
    const to = sp.get('to') ?? '';
    const query = sp.get('q') ?? '';
    const [holding, setHolding] = useState(null);
    const [logging, setLogging] = useState(null);
    const navigate = useNavigate();
    const casing = useGlossaryCasing();
    const eventMode = sp.get('events') === '1';
    const setParam = (key, value, replace = false)=>setSp((prev)=>{
            const n = new URLSearchParams(prev);
            if (value) n.set(key, value);
            else n.delete(key);
            return n;
        }, {
            replace
        });
    const today = useMemo(()=>todayISO(), []);
    const dueFrom = range === 'custom' ? from : today;
    const dueTo = range === 'custom' ? to : addDays(today, Number(range));
    // Contract-wide has no workstream id to ask for: read every team's dates and
    // keep the team-less ones below.
    const workstreamId = team === CONTRACT_WIDE ? '' : team;
    const inRange = useOccurrences(programId, {
        window: OccurrenceWindow.ALL,
        dueFrom,
        dueTo,
        workstreamId,
        includeProjected: false
    }, {
        enabled: !eventMode
    });
    const late = useOccurrences(programId, {
        window: OccurrenceWindow.ALL,
        states: [
            'late'
        ],
        workstreamId
    }, {
        enabled: !eventMode
    });
    // The dates projected from each event's plan: only the event list shows them.
    const projected = useOccurrences(programId, {
        window: OccurrenceWindow.ALL,
        states: [
            'waiting_on_event'
        ],
        includeProjected: true,
        workstreamId
    }, {
        enabled: eventMode
    });
    // Every date any rule minted, and each team's exercise lines: who owns a
    // deliverable's dates, which an award rule does not say.
    const allDates = useOccurrences(programId, {
        window: OccurrenceWindow.ALL
    });
    const details = useWorkstreamDetails(programId);
    // One row per event-driven (or on_request) schedule rule's deliverable and
    // the team that owns its dates, so a row stays once its event is logged.
    // Owners: a team's SOW rule is that team's; an award rule on an optional task
    // belongs to the teams its exercise lines authorize, and to any team already
    // holding a date it minted; with no owner it is contract-wide, its own team:
    // listed under Contract-wide (and All teams), never under a picked team.
    const eventRows = useMemo(()=>{
        const planned = new Map();
        for (const o of projected.data?.occurrences ?? []){
            if (!o.event?.projected || !o.dueOn) continue;
            const k = `${o.dutyId}:${o.event.kind}:${normalizeTeamId(o.workstreamId)}`;
            const cur = planned.get(k);
            if (!cur || o.dueOn < cur) planned.set(k, o.dueOn);
        }
        const datedBy = new Map();
        for (const o of allDates.data?.occurrences ?? []){
            const ws = normalizeTeamId(o.workstreamId);
            if (!o.ruleId || !ws) continue;
            datedBy.set(o.ruleId, (datedBy.get(o.ruleId) ?? new Set()).add(ws));
        }
        const lineTeams = new Map();
        for (const d of details.data ?? [])for (const l of d.exerciseLines)if (l.optionalTaskId && d.workstream) lineTeams.set(l.optionalTaskId, (lineTeams.get(l.optionalTaskId) ?? new Set()).add(d.workstream.id));
        const teamOf = new Map();
        for (const u of lk.undatedReasons)if (u.assignmentId) teamOf.set(u.assignmentId, u.workstreamId);
        const out = new Map();
        for (const r of lk.rules){
            const kind = r.eventKind;
            const request = kind === 'on_request';
            if (!kind || kind.startsWith('as_needed') || NOT_A_LOG_EVENT.test(kind)) continue;
            if (!request && r.kind !== 'event' && r.kind !== 'recurring') continue;
            const own = !!r.assignmentId;
            const teams = new Set(datedBy.get(r.id));
            if (own) {
                const t = teamOf.get(r.assignmentId);
                if (t) teams.add(t);
            } else if (r.optionalTaskId) for (const t of lineTeams.get(r.optionalTaskId) ?? [])teams.add(t);
            if (own && teams.size === 0) continue;
            for (const ws of teams.size ? teams : [
                ''
            ]){
                const owned = teams.size > 0;
                if (team === CONTRACT_WIDE ? owned : team && ws !== team) continue;
                const key = `${r.dutyId}:${kind}:${ws}`;
                if (out.has(key)) continue;
                const duty = lk.dutyById.get(r.dutyId);
                const name = eventLabelIn(kind, lk.duties, lk.rules, casing);
                const milestone = lk.duties.some((d)=>d.eventKind === kind);
                out.set(key, {
                    key,
                    dutyId: r.dutyId,
                    title: duty?.title ?? 'Untitled deliverable',
                    pwsRef: duty?.pwsRef ?? '',
                    clinLabel: dutyClinDisplay(duty?.dutyKey ?? '', clins).label,
                    teamId: ws,
                    teamName: lk.teamName(owned ? ws : ''),
                    request,
                    eventKind: kind,
                    verb: milestone ? `Mark ${name} held` : `Log ${name}`,
                    waitsOn: describeRule(r, (id)=>lk.dutyById.get(id)?.title),
                    planned: planned.get(`${r.dutyId}:${kind}:${ws}`) ?? ''
                });
            }
        }
        return [
            ...out.values()
        ].sort((a, b)=>a.title.localeCompare(b.title) || a.teamName.localeCompare(b.teamName));
    }, [
        lk,
        clins,
        team,
        casing,
        projected.data,
        allDates.data,
        details.data
    ]);
    const searchedEvents = useMemo(()=>{
        const q = query.trim().toLowerCase();
        return q ? eventRows.filter((r)=>[
                r.title,
                r.pwsRef,
                r.teamName,
                r.waitsOn
            ].some((v)=>v.toLowerCase().includes(q))) : eventRows;
    }, [
        eventRows,
        query
    ]);
    const eventSpecs = [
        {
            id: 'team',
            label: 'Team',
            value: (r)=>r.teamName
        },
        {
            id: 'title',
            label: 'Deliverable',
            value: (r)=>r.title
        },
        {
            id: 'clin',
            label: 'CLIN',
            value: (r)=>r.clinLabel
        },
        {
            id: 'waits',
            label: 'Waits on',
            value: (r)=>r.waitsOn
        }
    ];
    const eventView = useColumnView(eventSpecs, eventRows);
    const visibleEvents = eventView.apply(searchedEvents);
    const rows = useMemo(()=>{
        const seen = new Set();
        const occs = [
            ...late.data?.occurrences ?? [],
            ...inRange.data?.occurrences ?? []
        ].filter((o)=>!seen.has(o.id) && !!seen.add(o.id)).filter((o)=>team !== CONTRACT_WIDE || normalizeTeamId(o.workstreamId) === '').sort(byUrgency);
        return occs.map((o)=>{
            const duty = lk.dutyById.get(o.dutyId);
            const periodId = resolveOccurrencePeriodId(o, lk.periods, lk.exercises);
            return {
                occ: o,
                dueOn: o.dueOn,
                title: duty?.title ?? 'Untitled deliverable',
                pwsRef: duty?.pwsRef ?? '',
                dutyKey: duty?.dutyKey ?? '',
                clinLabel: occurrenceClinDisplay(duty?.dutyKey ?? '', periodId, lk, clins).label,
                teamId: normalizeTeamId(o.workstreamId),
                teamName: lk.teamName(o.workstreamId),
                statusState: o.status?.state,
                periodId
            };
        });
    }, [
        late.data,
        inRange.data,
        team,
        lk,
        clins
    ]);
    const searched = useMemo(()=>filterDateRows(rows, {
            q: query
        }, clins), [
        rows,
        query,
        clins
    ]);
    const dateSpecs = [
        {
            id: 'due',
            label: 'Due',
            kind: 'date',
            value: (r)=>cmDate(r.occ.dueOn, 'Not stated'),
            sortValue: (r)=>r.occ.dueOn
        },
        {
            id: 'team',
            label: 'Team',
            value: (r)=>r.teamName
        },
        {
            id: 'title',
            label: 'Deliverable',
            value: (r)=>r.title ?? ''
        },
        {
            id: 'clin',
            label: 'CLIN',
            value: (r)=>r.clinLabel
        },
        {
            id: 'latest',
            label: 'Latest submission',
            kind: 'date',
            value: (r)=>cmDate(r.occ.latestSubmission?.submittedOn, 'None'),
            sortValue: (r)=>r.occ.latestSubmission?.submittedOn ?? ''
        },
        {
            id: 'status',
            label: 'Status',
            value: (r)=>isOccurrenceState(r.statusState ?? '') ? STATE_SHORT_LABEL[r.statusState] : r.statusState ?? ''
        }
    ];
    const dateView = useColumnView(dateSpecs, rows);
    const visible = dateView.apply(searched);
    const columns = [
        {
            id: 'due',
            header: dateView.header('due'),
            width: 140,
            cell: (r)=>/*#__PURE__*/ _jsxs("span", {
                    className: "whitespace-nowrap tabular-nums",
                    children: [
                        cmDate(r.occ.dueOn, 'Not stated'),
                        r.occ.label ? /*#__PURE__*/ _jsx("span", {
                            className: "ml-2 text-muted-foreground",
                            children: r.occ.label
                        }) : null
                    ]
                })
        },
        {
            id: 'team',
            header: dateView.header('team'),
            width: 160,
            cell: (r)=>r.teamName
        },
        {
            id: 'title',
            header: dateView.header('title'),
            cell: (r)=>r.title
        },
        {
            id: 'clin',
            header: dateView.header('clin'),
            width: 130,
            cell: (r)=>r.clinLabel ? /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "info",
                    className: "tabular-nums",
                    "data-testid": Testids.cmDuty.dateClin,
                    children: r.clinLabel
                }) : /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: "—"
                })
        },
        {
            id: 'latest',
            header: dateView.header('latest'),
            headerClassName: 'whitespace-nowrap',
            cell: (r)=>/*#__PURE__*/ _jsx(CmLatestSubmission, {
                    occ: r.occ
                })
        },
        {
            id: 'status',
            header: dateView.header('status'),
            width: 180,
            cell: (r)=>/*#__PURE__*/ _jsx(CmOccurrenceStatus, {
                    programId: programId,
                    occ: r.occ,
                    finalReviewTier: lk.dutyById.get(r.occ.dutyId)?.finalReviewTier
                })
        }
    ];
    // A milestone (a duty that dates an event) is met by marking it held, never
    // filed: its verb is Mark held. Nothing to do on a date already closed out.
    const rowActions = (r)=>{
        if (!access.canWrite || !isOpenState(r.statusState ?? '')) return null;
        const milestone = !!lk.dutyById.get(r.occ.dutyId)?.eventKind;
        return /*#__PURE__*/ _jsx(SpecraButton, {
            size: "sm",
            onClick: (e)=>{
                e.stopPropagation();
                if (milestone) setHolding(r);
                else params.open('submit', r.occ.id);
            },
            "data-testid": milestone ? Testids.cmUpcoming.markHeld : Testids.cmUpcoming.submit,
            children: milestone ? 'Mark held' : '+ Submit'
        });
    };
    const eventColumns = [
        {
            id: 'team',
            header: eventView.header('team'),
            width: 160,
            cell: (r)=>r.teamName
        },
        {
            id: 'title',
            header: eventView.header('title'),
            cell: (r)=>/*#__PURE__*/ _jsx(Link, {
                    className: "underline-offset-2 hover:underline",
                    to: `/delivery/programs/${programId}/deliverables/${r.dutyId}`,
                    onClick: (e)=>e.stopPropagation(),
                    children: r.title
                })
        },
        {
            id: 'clin',
            header: eventView.header('clin'),
            width: 130,
            cell: (r)=>r.clinLabel ? /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "info",
                    className: "tabular-nums",
                    "data-testid": Testids.cmDuty.dateClin,
                    children: r.clinLabel
                }) : /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: "—"
                })
        },
        {
            id: 'waits',
            header: eventView.header('waits'),
            cell: (r)=>/*#__PURE__*/ _jsxs("span", {
                    children: [
                        r.waitsOn,
                        r.planned ? /*#__PURE__*/ _jsxs("span", {
                            className: "ml-2 text-muted-foreground",
                            children: [
                                "planned ",
                                cmDate(r.planned)
                            ]
                        }) : null
                    ]
                })
        }
    ];
    // Match the deliverable page: a request is logged for the deliverable; any
    // other event with its kind and team preset (a milestone reads "Mark … held").
    const eventActions = (r)=>{
        if (!access.canWrite) return null;
        const milestone = r.verb.startsWith('Mark ');
        return /*#__PURE__*/ _jsx(SpecraButton, {
            size: "sm",
            onClick: (e)=>{
                e.stopPropagation();
                setLogging(r.request ? {
                    kind: 'on_request',
                    team: r.teamId,
                    milestone: false,
                    dutyId: r.dutyId
                } : {
                    kind: r.eventKind,
                    team: r.teamId,
                    milestone,
                    dutyId: ''
                });
            },
            "data-testid": r.request ? Testids.cmUpcoming.logRequest : Testids.cmUpcoming.logEvent,
            children: r.request ? 'Log government request' : r.verb
        });
    };
    const holdingDuty = holding ? lk.dutyById.get(holding.occ.dutyId) : undefined;
    const rangeControls = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                label: "Range",
                value: range,
                options: [
                    {
                        value: '7',
                        label: '7 days',
                        disabled: eventMode
                    },
                    {
                        value: '14',
                        label: '14 days',
                        disabled: eventMode
                    },
                    {
                        value: '30',
                        label: '30 days',
                        disabled: eventMode
                    },
                    {
                        value: 'custom',
                        label: 'Custom',
                        disabled: eventMode
                    }
                ],
                onValueChange: (v)=>setParam('range', v === DEFAULT_RANGE ? '' : v),
                testId: Testids.cmUpcoming.range
            }),
            range === 'custom' ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraDateField, {
                        picker: "native",
                        label: "From",
                        value: from,
                        disabled: eventMode,
                        onValueChange: (v)=>setParam('from', v),
                        "data-testid": Testids.cmUpcoming.from
                    }),
                    /*#__PURE__*/ _jsx(SpecraDateField, {
                        picker: "native",
                        label: "To",
                        value: to,
                        disabled: eventMode,
                        onValueChange: (v)=>setParam('to', v),
                        "data-testid": Testids.cmUpcoming.to
                    })
                ]
            }) : null
        ]
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraListPage, {
                "data-testid": Testids.cmDuties.page,
                header: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        header,
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-wrap items-end gap-3 rounded-lg border bg-muted/40 p-3",
                            "data-testid": Testids.cmUpcoming.controls,
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraSelect, {
                                    "aria-label": "Team",
                                    className: "w-56",
                                    value: team,
                                    onChange: (e)=>setParam('team', e.target.value),
                                    "data-testid": Testids.cmUpcoming.team,
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "",
                                            children: "All teams"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: CONTRACT_WIDE,
                                            children: "Contract-wide"
                                        }),
                                        lk.workstreams.map((w)=>/*#__PURE__*/ _jsx("option", {
                                                value: w.id,
                                                children: w.name
                                            }, w.id))
                                    ]
                                }),
                                eventMode ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                                    content: "Turn off Event-driven to see the schedule",
                                    children: /*#__PURE__*/ _jsx("span", {
                                        className: "flex flex-wrap items-center gap-3",
                                        children: rangeControls
                                    })
                                }) : rangeControls,
                                /*#__PURE__*/ _jsx(SpecraInput, {
                                    type: "search",
                                    "aria-label": "Search upcoming deliverables",
                                    className: "w-64",
                                    placeholder: "Search title, PWS or team…",
                                    value: query,
                                    onChange: (e)=>setParam('q', e.target.value, true),
                                    "data-testid": Testids.cmUpcoming.search
                                }),
                                /*#__PURE__*/ _jsxs(SpecraButton, {
                                    variant: eventMode ? 'default' : 'secondary',
                                    "aria-pressed": eventMode,
                                    onClick: ()=>setParam('events', eventMode ? '' : '1'),
                                    "data-testid": Testids.cmUpcoming.eventToggle,
                                    children: [
                                        "Event-driven (",
                                        eventRows.length,
                                        ")"
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                toolbarProps: {
                    search: false,
                    resultSummary: eventMode ? `${visibleEvents.length} waiting` : `${visible.length} due`
                },
                children: eventMode ? /*#__PURE__*/ _jsx(CmTable, {
                    caption: "Event-driven deliverables",
                    rows: visibleEvents,
                    columns: eventColumns,
                    rowKey: (r)=>r.key,
                    rowTestId: Testids.cmUpcoming.eventRow,
                    isLoading: lk.isLoading,
                    onRowClick: (r)=>navigate(`/delivery/programs/${programId}/deliverables/${r.dutyId}`),
                    rowChevron: true,
                    getRowLabel: (r)=>r.title,
                    rowActions: eventActions,
                    empty: /*#__PURE__*/ _jsx("div", {
                        "data-testid": Testids.cmUpcoming.eventEmpty,
                        children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                            framed: true,
                            title: "No event-driven deliverables waiting for this team",
                            action: clearAllAction(eventView)
                        })
                    })
                }) : /*#__PURE__*/ _jsx(CmTable, {
                    caption: "Upcoming deliverables",
                    rows: visible,
                    columns: columns,
                    rowKey: (r)=>r.occ.id,
                    rowTestId: Testids.cmUpcoming.row,
                    isLoading: lk.isLoading || inRange.isLoading || late.isLoading,
                    error: inRange.error ?? late.error,
                    onRetry: ()=>{
                        void inRange.refetch();
                        void late.refetch();
                    },
                    onRowClick: (r)=>params.open('occ', r.occ.id),
                    getRowLabel: (r)=>r.title ?? '',
                    rowActions: rowActions,
                    empty: /*#__PURE__*/ _jsx("div", {
                        "data-testid": Testids.cmUpcoming.empty,
                        children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                            framed: true,
                            title: "Nothing due in this range",
                            action: clearAllAction(dateView)
                        })
                    })
                })
            }),
            logging ? /*#__PURE__*/ _jsx(LogEventDrawer, {
                programId: programId,
                kind: logging.kind,
                milestone: logging.milestone,
                workstreamId: logging.team,
                dutyId: logging.dutyId,
                onClose: ()=>setLogging(null)
            }) : null,
            holding ? /*#__PURE__*/ _jsx(LogEventDrawer, {
                programId: programId,
                kind: holdingDuty?.eventKind,
                milestone: true,
                workstreamId: normalizeTeamId(holding.occ.workstreamId),
                onClose: ()=>setHolding(null)
            }) : null
        ]
    });
}
